import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Lottie from "lottie-react";
import LottieBoundary from "@/components/LottieBoundary";
import { api, picUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWS } from "@/context/WSContext";
import { Zap, Crown, Sparkles, Diamond, Download, Heart, Loader2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { generateGiftMp4 } from "@/lib/generateGiftMp4";

/**
 * Tier configuration for the full-screen boost takeover.
 * Each tier defines a unique color palette, central icon, and CSS animation.
 * The animation escalates with the amount — a $50 tip feels dramatically
 * different than a $5 one, TikTok-gift style.
 *
 * `lottie` is the path to a full-screen Lottie JSON in /public/lottie/gifts.
 * Missing file? Component falls back to the CSS particle burst.
 */
const TIERS = [
  {
    minCents: 100,
    label: "BLOOM",
    accent: "#FF007F",
    accent2: "#FFB800",
    Icon: Heart,
    subtitle: "petal.dropped",
    burstCount: 8,
    duration: 4000,
    lottie: "/lottie/gifts/tier1.json",
    // BLOOM's Lottie has a very long dark intro (~85% of the timeline is
    // empty/dark reveal). Sample only the final "payoff" so the GIF is
    // visually rich instead of mostly black.
    gifSampleStart: 0.85,
    gifSampleEnd: 1.0,
  },
  {
    minCents: 300,
    label: "SUPERNOVA",
    accent: "#39FF14",
    accent2: "#00FFFF",
    Icon: Sparkles,
    subtitle: "boost.received",
    burstCount: 12,
    duration: 5000,
    lottie: "/lottie/gifts/tier2.json",
  },
  {
    minCents: 600,
    label: "PLASMA STORM",
    accent: "#00FFFF",
    accent2: "#FF007F",
    Icon: Zap,
    subtitle: "energy.surge.detected",
    burstCount: 18,
    duration: 5500,
    lottie: "/lottie/gifts/tier3.json",
  },
  {
    minCents: 900,
    label: "GHOST WHALE",
    accent: "#FF007F",
    accent2: "#39FF14",
    Icon: Diamond,
    subtitle: "elite.tier.unlocked",
    burstCount: 30,
    duration: 6500,
    lottie: "/lottie/gifts/tier4.json",
  },
  {
    minCents: 1200,
    label: "MATRIX GOD",
    accent: "#FF007F",
    accent2: "#FFB800",
    Icon: Crown,
    subtitle: "reality.rewritten",
    burstCount: 45,
    duration: 8000,
    lottie: "/lottie/gifts/tier5.json",
  },
];

const tierFor = (amountCents) => {
  let t = TIERS[0];
  for (const tier of TIERS) if (amountCents >= tier.minCents) t = tier;
  return t;
};

/**
 * Boost-takeover video layer. Tries to autoplay with SOUND first; if the
 * browser blocks it (viewer just landed, no recent gesture), falls back
 * to muted and exposes an UNMUTE button so the user can flip it in one
 * tap. This mirrors how TikTok/IG handle their unmute affordance.
 */
function CustomVideoLayer({ src, loop = false, onEnded }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const [needsUnmute, setNeedsUnmute] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    v.muted = false;
    v.volume = 1.0;
    // Attempt sound-on autoplay. If the browser rejects, fall back to
    // muted playback (which is always allowed) and surface the UNMUTE
    // button so the viewer can restore audio on their next tap.
    const p = v.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => {
        try {
          v.muted = true;
          setMuted(true);
          setNeedsUnmute(true);
          const p2 = v.play();
          if (p2 && typeof p2.catch === "function") p2.catch(() => {});
        } catch { /* noop */ }
      });
    }
    return undefined;
  }, [src]);

  const handleUnmute = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    try {
      v.muted = false;
      v.volume = 1.0;
      setMuted(false);
      setNeedsUnmute(false);
      // Some browsers pause when muted state changes; kick it just in case.
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch { /* noop */ }
  };

  return (
    <>
      <div
        data-testid="gift-video-layer"
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
      >
        <video
          ref={videoRef}
          data-testid="gift-custom-mp4"
          src={src}
          autoPlay
          muted={muted}
          playsInline
          preload="auto"
          loop={loop}
          onEnded={loop ? undefined : onEnded}
          className="w-full h-full object-contain"
        />
      </div>
      {needsUnmute && (
        <button
          type="button"
          data-testid="gift-unmute-btn"
          onClick={handleUnmute}
          className="absolute top-5 right-5 z-10 flex items-center gap-1.5 bg-black/70 border border-white/30 hover:border-[#FFC300] hover:bg-black/90 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-white pointer-events-auto"
        >
          <VolumeX className="h-3 w-3" /> tap.to.unmute
        </button>
      )}
    </>
  );
}

/**
 * Save-and-share button shown at the bottom of the gift takeover.
 * Renders the branded MP4/WebM, then invokes the native Web Share sheet
 * so the user can pick their target (TikTok, IG, WhatsApp, X, etc.) from
 * the OS share picker. On desktop / unsupported browsers we fall back to
 * a plain download.
 */
function GiftShareBar({ gift, usd, tier, lottieData, customVideoUrl, savingGif, setSavingGif, savingGifRef, onFinished }) {
  const captionText = `I just got GIFTED $${usd} on ios.matrixshopping.com 💰 #matrixshopping #boost`;

  // Background pre-render: as soon as the takeover shows the Lottie,
  // we start encoding the video. When the user taps SAVE, the File is
  // already ready and we can call navigator.share() SYNCHRONOUSLY
  // inside the click handler — which is the only way Chrome's Web
  // Share API will accept it (was throwing "Must be handling a user
  // gesture" when we awaited the encode inside the click handler).
  //
  // preStatus: 'idle' → 'encoding' → 'ready' | 'error'
  const [preStatus, setPreStatus] = useState("idle");
  const [preFile, setPreFile] = useState(null);
  const preBlobUrlRef = useRef(null);
  const cancelRef = useRef(false);
  // Mirror refs so the slow-path polling loop can observe live updates
  // without re-triggering the effect that spawned it.
  const preFileRef = useRef(null);
  const preStatusRef = useRef("idle");
  useEffect(() => { preFileRef.current = preFile; }, [preFile]);
  useEffect(() => { preStatusRef.current = preStatus; }, [preStatus]);

  useEffect(() => {
    // Only pre-render for real received gifts; skip preview events (no id).
    // For custom MP4 tiers we don't need Lottie data — the encoder samples
    // the video directly. For Lottie tiers we wait for the JSON to load.
    if (!gift.id) return undefined;
    if (!customVideoUrl && !lottieData) return undefined;
    if (preStatus !== "idle") return undefined;
    let started = false;
    // Small delay so the entrance animation isn't fighting for main-thread
    // time with the encoder — starts ~400ms after the takeover appears.
    const t = window.setTimeout(async () => {
      started = true;
      setPreStatus("encoding");
      try {
        const { blob, ext, mime } = await generateGiftMp4({
          gift: {
            ...gift,
            tipper_picture: gift.tipper_picture ? picUrl(gift.tipper_picture) : null,
          },
          tier,
          lottieData,
          customVideoUrl,
        });
        if (cancelRef.current) return;
        const fname = `matrix-boost-${(gift.id || "").slice(0, 8)}.${ext}`;
        const file = new File([blob], fname, { type: mime });
        // Keep a blob URL around in case we need to fall back to download.
        preBlobUrlRef.current = URL.createObjectURL(blob);
        setPreFile(file);
        setPreStatus("ready");
      } catch (err) {
        if (!cancelRef.current) setPreStatus("error");
      }
    }, 400);
    return () => {
      window.clearTimeout(t);
      if (!started) return;
      // Mark cancelled so the pending encode's finally doesn't touch state
      cancelRef.current = true;
    };
  }, [gift.id, lottieData, customVideoUrl]);

  // Free the pre-render blob URL when the component unmounts.
  useEffect(() => {
    return () => {
      if (preBlobUrlRef.current) {
        try { URL.revokeObjectURL(preBlobUrlRef.current); } catch { /* ignore */ }
        preBlobUrlRef.current = null;
      }
    };
  }, []);

  // Fire-and-forget attribution ping — see share-intent endpoint.
  const recordShareIntent = (platform) => {
    if (!gift.id) return;
    try {
      api.post(`/gifts/${gift.id}/share-intent`, { platform }).catch(() => {});
    } catch { /* ignore */ }
  };

  // Fallback filename when the encoded file isn't attached to state yet
  // (e.g. the slow-path download branch). The extension is refined once
  // the encode completes and we know the actual mime the browser picked.
  const fallbackFilename = `matrix-boost-${(gift.id || "").slice(0, 8)}.webm`;

  const doDownloadFallback = () => {
    if (!preBlobUrlRef.current) return;
    const a = document.createElement("a");
    a.href = preBlobUrlRef.current;
    a.download = preFile?.name || fallbackFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    recordShareIntent("download");
  };

  // Called synchronously inside the click event handler. Returns quickly.
  // If pre-render is ready, navigator.share() runs immediately inside the
  // user gesture (the tap that just fired). If still encoding, we set
  // savingGif so the button flips to a "waiting" state; the effect below
  // will auto-fire share the moment the encode finishes IF the user's
  // gesture is still fresh (within ~1-2s). Otherwise we prompt again.
  const handleSave = (e) => {
    e?.stopPropagation();
    if (savingGif) return;
    // Guard the auto-dismiss timer.
    savingGifRef.current = true;
    setSavingGif(true);

    if (preStatus === "ready" && preFile) {
      // FAST PATH — this is what fixes the "Failed to execute 'share'"
      // error: navigator.share() runs synchronously inside the click.
      // We intentionally do NOT gate on `navigator.canShare({files})`.
      // Some browsers (mobile Safari occasionally, Chromium on certain
      // older Android builds) return `false` from canShare for video
      // mimes but still successfully open the share sheet when share()
      // is called. Try the share and fall back to download only on a
      // real rejection (not AbortError, which just means "user cancelled").
      if (navigator.share) {
        navigator.share({
          files: [preFile],
          title: `+$${usd} boost`,
          text: captionText,
        }).then(() => {
          recordShareIntent("native");
        }).catch((err) => {
          if (err?.name === "AbortError") return; // user dismissed the sheet
          // Some browsers reject with NotAllowedError when files aren't
          // supported — try again without the file (URL/text only) so at
          // least a share sheet opens with the caption.
          const isFileError = err?.name === "NotAllowedError" || String(err?.message || "").toLowerCase().includes("permission");
          if (isFileError) {
            navigator.share({
              title: `+$${usd} boost`,
              text: captionText,
              url: window.location.href,
            }).catch(() => doDownloadFallback());
            return;
          }
          doDownloadFallback();
        }).finally(() => {
          savingGifRef.current = false;
          setSavingGif(false);
          onFinished?.();
        });
        return;
      }
      // navigator.share missing entirely (desktop Firefox, some in-app browsers).
      doDownloadFallback();
      toast.success("Video saved — share it on your socials");
      savingGifRef.current = false;
      setSavingGif(false);
      onFinished?.();
      return;
    }

    // SLOW PATH — encode isn't ready yet. Poll for the in-flight encode
    // to complete (max ~20s) rather than kicking off a duplicate render.
    // Once ready, show a toast with a SHARE action button — the user's
    // tap on that button gives us the fresh gesture navigator.share() needs.
    const toastId = toast.loading("Rendering your video…");
    const waitForReady = async () => {
      try {
        // Poll preFileRef every 250ms up to 20s.
        let file = preFileRef.current;
        const startedAt = Date.now();
        while (!file && Date.now() - startedAt < 20000) {
          await new Promise((r) => setTimeout(r, 250));
          file = preFileRef.current;
          if (!file && preStatusRef.current === "error") break;
        }
        // If polling ran out AND still no file, fall back to a fresh encode.
        if (!file) {
          const { blob, ext, mime } = await generateGiftMp4({
            gift: {
              ...gift,
              tipper_picture: gift.tipper_picture ? picUrl(gift.tipper_picture) : null,
            },
            tier,
            lottieData,
            customVideoUrl,
          });
          const fname = `matrix-boost-${(gift.id || "").slice(0, 8)}.${ext}`;
          file = new File([blob], fname, { type: mime });
          preBlobUrlRef.current = URL.createObjectURL(blob);
          setPreFile(file);
          setPreStatus("ready");
        }
        // File is ready — release the button lock so the user can tap
        // the main SHARE.VIDEO button on the takeover (which fires share
        // synchronously inside the click and is more reliable than the
        // toast action on Android in-app browsers).
        savingGifRef.current = false;
        setSavingGif(false);
        if (navigator.share) {
          const shareFile = file;
          // The takeover's main SHARE.VIDEO button is now tappable again
          // (savingGif was released above). Also show a small "READY"
          // toast with SHARE fallback in case the user is looking at the
          // toast rather than the button. Auto-dismisses in 6s so the UI
          // doesn't stay busy.
          toast.success("Ready — tap SHARE.VIDEO", {
            id: toastId,
            duration: 6000,
            action: {
              label: "SHARE",
              onClick: async () => {
                try {
                  await navigator.share({
                    files: [shareFile],
                    title: `+$${usd} boost`,
                    text: captionText,
                  });
                  recordShareIntent("native");
                } catch (err) {
                  if (err?.name === "AbortError") return;
                  const isFileError = err?.name === "NotAllowedError" || String(err?.message || "").toLowerCase().includes("permission");
                  if (isFileError) {
                    try {
                      await navigator.share({
                        title: `+$${usd} boost`,
                        text: captionText,
                        url: window.location.href,
                      });
                      return;
                    } catch { /* fall through to download */ }
                  }
                  doDownloadFallback();
                }
              },
            },
          });
        } else {
          doDownloadFallback();
          toast.success("Video saved — share it on your socials", { id: toastId });
        }
      } catch (err) {
        toast.error(err?.message || "Could not save gift video", { id: toastId });
        savingGifRef.current = false;
        setSavingGif(false);
      }
      // Note: NO `finally` block resetting savingGif here — we reset it
      // early (right after the file is ready) so the main SHARE.VIDEO
      // button on the takeover can be tapped immediately.
    };
    waitForReady();
  };

  return (
    <div
      className="relative z-10 mt-6 flex items-center justify-center"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        data-testid="gift-save-btn"
        disabled={savingGif}
        onClick={handleSave}
        className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-5 py-2.5 border-2 hover:shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all disabled:opacity-60"
        style={{ borderColor: tier.accent, color: tier.accent }}
      >
        {savingGif ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
            {preStatus === "ready" ? "opening.share…" : "rendering.video…"}
          </>
        ) : (
          <>
            <Download className="h-3.5 w-3.5" strokeWidth={2} />
            {preStatus === "ready" ? "share.video" : "save.video.share.on.socials"}
          </>
        )}
      </button>
    </div>
  );
}


function Takeover({ gift, onDismiss, customAnimations }) {
  const tier = tierFor(gift.amount_cents);
  const usd = (gift.amount_cents / 100).toFixed(2);
  const [visible, setVisible] = useState(false);
  const [lottieData, setLottieData] = useState(null);
  const [savingGif, setSavingGif] = useState(false);
  // Ref mirror of savingGif, updated synchronously inside the Save
  // handler BEFORE React commits. This closes a subtle race where the
  // auto-dismiss `setTimeout` fires in the tiny window between the user
  // tapping Save and React re-rendering the effect that clears it —
  // observed with $1 BLOOM (4s duration) and $7 PLASMA STORM (5.5s)
  // when the user watches the animation first and then taps.
  const savingGifRef = useRef(false);

  // Admin-uploaded MP4 override: prefer any URL bundled inline on the
  // gift (used by the /admin/gifts preview button on unsaved rows). Else
  // pick the highest configured tier whose min_cents does not exceed
  // the tip. When present, this MP4 replaces the Lottie animation as
  // the takeover visual.
  const customVideoUrl = useMemo(() => {
    if (gift.preview_video_url) return gift.preview_video_url;
    if (!Array.isArray(customAnimations) || customAnimations.length === 0) return null;
    let matched = null;
    for (const c of customAnimations) {
      if (gift.amount_cents >= Number(c.min_cents)) matched = c;
    }
    return matched?.video_url || null;
  }, [customAnimations, gift.amount_cents, gift.preview_video_url]);

  // Load the tier's Lottie JSON. Missing/404 → we silently fall back to
  // the CSS particle burst so the animation never looks broken.
  // `?v=1` busts any stale 404 cached from before the files were uploaded.
  useEffect(() => {
    if (customVideoUrl) return undefined; // MP4 supersedes Lottie
    if (!tier.lottie) return undefined;
    let cancelled = false;
    fetch(`${tier.lottie}?v=2`, { cache: "default" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((j) => { if (!cancelled) setLottieData(j); })
      .catch(() => { /* fallback CSS bursts play instead */ });
    return () => { cancelled = true; };
  }, [tier.lottie, customVideoUrl]);

  useEffect(() => {
    // trigger CSS entrance
    const raf = requestAnimationFrame(() => setVisible(true));
    // Real received gifts (with `gift.id`) show the SAVE button and
    // encode a shareable video in the background — those must stay on
    // screen until the user taps SAVE or dismisses manually. Otherwise
    // the encoder gets cancelled mid-render and the share sheet never
    // appears. Previews / gifts without an id still auto-dismiss so
    // stress-testing doesn't leave orphan takeovers around.
    if (gift.id && gift.mode !== "sent") {
      return () => cancelAnimationFrame(raf);
    }
    const dismissAfter = customVideoUrl ? 12000 : tier.duration;
    let t = null;
    if (!savingGif) {
      t = setTimeout(() => {
        if (savingGifRef.current) return; // save started while we were queued
        onDismiss();
      }, dismissAfter);
    }
    return () => {
      cancelAnimationFrame(raf);
      if (t) clearTimeout(t);
    };
  }, [tier.duration, onDismiss, savingGif, customVideoUrl, gift.id, gift.mode]);

  // Randomized burst particles positions — memoized so they don't re-shuffle
  const bursts = useMemo(() => {
    return Array.from({ length: tier.burstCount }, (_, i) => ({
      key: i,
      angle: (i / tier.burstCount) * 360 + Math.random() * 20,
      distance: 40 + Math.random() * 45,
      delay: Math.random() * 400,
      duration: 1400 + Math.random() * 1200,
      size: 8 + Math.random() * 18,
      char: ["⚡", "✦", "◆", "★", "◈", "✧"][Math.floor(Math.random() * 6)],
      color: Math.random() > 0.5 ? tier.accent : tier.accent2,
    }));
  }, [tier]);

  const { Icon } = tier;

  return (
    <div
      data-testid="gift-takeover"
      // While a GIF encode is in-flight (`savingGif`), a stray tap on the
      // backdrop MUST NOT dismiss the takeover — the encoder is sampling
      // the live SVG frame-by-frame, so unmounting mid-encode kills the
      // pipeline and the "Rendering your GIF…" toast just spins forever
      // before the user returns to the feed with no share dialog. This
      // was the reproducible failure the user filmed on $1 BLOOM and $7
      // PLASMA STORM (both are 2.7-4.7 MB Lotties which take the encoder
      // 6-10 seconds — plenty of time for an accidental tap.)
      onClick={savingGif ? undefined : onDismiss}
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center transition-all duration-500 ${
        savingGif ? "cursor-wait" : "cursor-pointer"
      } ${visible ? "opacity-100" : "opacity-0"}`}
      style={{
        background: `radial-gradient(circle at center, ${tier.accent}22 0%, #000 60%, #000 100%)`,
      }}
    >
      {/* Custom MP4 takeover (admin-uploaded) — takes precedence over Lottie.
          We try to play with sound; if the browser blocks autoplay-with-audio,
          the video is muted as a fallback and the viewer can tap UNMUTE.
          For real received gifts we LOOP the source so the takeover stays
          on-screen while the shareable MP4 finishes encoding (encoder needs
          roughly the same wall-clock time as the source clip). */}
      {customVideoUrl && (
        <CustomVideoLayer
          src={customVideoUrl}
          loop={!!(gift.id && gift.mode !== "sent")}
          onEnded={() => { if (!savingGifRef.current) onDismiss(); }}
        />
      )}

      {/* Full-screen Lottie animation (if the JSON is available) */}
      {!customVideoUrl && lottieData && (
        <LottieBoundary>
          <div
            data-testid="gift-lottie-layer"
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            style={{ opacity: 0.9 }}
          >
            <Lottie
              animationData={lottieData}
              loop={false}
              autoplay
              style={{ width: "min(120vw, 120vh)", height: "min(120vw, 120vh)" }}
            />
          </div>
        </LottieBoundary>
      )}

      {/* Grid backdrop */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `linear-gradient(${tier.accent2}22 1px, transparent 1px), linear-gradient(90deg, ${tier.accent2}22 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
          animation: "gift-grid-drift 8s linear infinite",
        }}
      />

      {/* Radial pulse rings */}
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="absolute rounded-full border-2 pointer-events-none"
          style={{
            width: 200,
            height: 200,
            borderColor: tier.accent,
            animation: `gift-pulse-ring ${1.6 + i * 0.3}s ${i * 0.35}s ease-out infinite`,
            opacity: 0,
          }}
        />
      ))}

      {/* Central icon */}
      <div
        className="relative z-10"
        style={{
          animation: visible ? "gift-icon-pop 1s cubic-bezier(0.34, 1.56, 0.64, 1)" : "none",
          filter: `drop-shadow(0 0 30px ${tier.accent})`,
        }}
      >
        <Icon
          className="h-24 w-24 md:h-32 md:w-32"
          strokeWidth={1.5}
          style={{ color: tier.accent }}
        />
      </div>

      {/* Radiating particles */}
      {bursts.map((b) => (
        <span
          key={b.key}
          className="absolute font-display font-black pointer-events-none"
          style={{
            fontSize: b.size,
            color: b.color,
            textShadow: `0 0 12px ${b.color}`,
            transform: `translate(${Math.cos((b.angle * Math.PI) / 180) * b.distance}vmin, ${Math.sin((b.angle * Math.PI) / 180) * b.distance}vmin)`,
            animation: `gift-burst ${b.duration}ms ${b.delay}ms cubic-bezier(0.15, 0.65, 0.55, 1) forwards`,
            opacity: 0,
          }}
        >
          {b.char}
        </span>
      ))}

      {/* Tier label */}
      <p
        className="relative z-10 mt-6 font-mono text-xs md:text-sm uppercase tracking-[0.5em] opacity-80"
        style={{ color: tier.accent2, textShadow: `0 0 12px ${tier.accent2}` }}
      >
        // {tier.subtitle}
      </p>
      <h1
        data-testid="gift-tier-label"
        className="relative z-10 mt-2 font-display font-black text-5xl md:text-7xl glitch text-center px-4"
        data-text={tier.label}
        style={{ color: tier.accent }}
      >
        {tier.label}
      </h1>

      {/* Amount */}
      <div
        className="relative z-10 mt-8 flex flex-col items-center"
        style={{
          animation: visible ? "gift-amount-slide 0.9s 0.2s cubic-bezier(0.16, 1, 0.3, 1) both" : "none",
        }}
      >
        <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">
          {gift.mode === "sent" ? "you.sent" : "you.received"}
        </p>
        <p
          data-testid="gift-amount"
          className="font-display font-black text-white text-6xl md:text-8xl mt-1 leading-none"
          style={{ textShadow: `0 0 30px ${tier.accent}` }}
        >
          {gift.mode === "sent" ? "" : "+"}${usd}
        </p>
        <p className="font-mono text-sm text-white/60 mt-2">
          {Math.floor(gift.amount_cents / 2).toLocaleString()} coins →{" "}
          <span style={{ color: tier.accent }}>
            {gift.mode === "sent" ? "delivered" : "your.wallet"}
          </span>
        </p>
      </div>

      {/* Tipper */}
      <div
        className="relative z-10 mt-8 flex items-center gap-3"
        style={{
          animation: visible ? "gift-tipper-fade 0.8s 0.5s ease-out both" : "none",
        }}
      >
        {gift.tipper_picture ? (
          <img
            src={picUrl(gift.tipper_picture)}
            alt=""
            className="h-10 w-10 object-cover"
            style={{ border: `2px solid ${tier.accent}` }}
          />
        ) : (
          <div
            className="h-10 w-10 flex items-center justify-center font-display font-bold text-lg"
            style={{ background: `${tier.accent}22`, border: `2px solid ${tier.accent}`, color: tier.accent }}
          >
            {(gift.tipper_name || "?")[0]?.toUpperCase()}
          </div>
        )}
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
            {gift.mode === "sent" ? "to" : "from"}
          </p>
          <p className="text-white font-semibold text-lg">
            {gift.tipper_name || "Anonymous"}
          </p>
        </div>
      </div>

      {gift.message && (
        <p className="relative z-10 mt-4 px-6 max-w-md text-center text-white/70 italic text-sm">
          “{gift.message}”
        </p>
      )}

      {gift.id && gift.mode !== "sent" && (
        <GiftShareBar
          gift={gift}
          usd={usd}
          tier={tier}
          lottieData={lottieData}
          customVideoUrl={customVideoUrl}
          savingGif={savingGif}
          setSavingGif={setSavingGif}
          savingGifRef={savingGifRef}
          onFinished={onDismiss}
        />
      )}

      <p className="absolute bottom-8 z-10 font-mono text-[10px] uppercase tracking-widest text-white/30">
        tap.anywhere.to.dismiss
      </p>

      {/* Keyframes injected inline (component-local) */}
      <style>{`
        @keyframes gift-grid-drift {
          from { background-position: 0 0, 0 0; }
          to { background-position: 40px 40px, 40px 40px; }
        }
        @keyframes gift-pulse-ring {
          0% { transform: scale(0.4); opacity: 0.7; }
          100% { transform: scale(2.6); opacity: 0; }
        }
        @keyframes gift-icon-pop {
          0% { transform: scale(0) rotate(-180deg); opacity: 0; }
          60% { transform: scale(1.2) rotate(15deg); opacity: 1; }
          100% { transform: scale(1) rotate(0); opacity: 1; }
        }
        @keyframes gift-burst {
          0% { transform: translate(0, 0) scale(0); opacity: 1; }
          20% { opacity: 1; }
          100% {
            transform: translate(
              var(--tw-translate-x, 0),
              var(--tw-translate-y, 0)
            ) scale(0.4);
            opacity: 0;
          }
        }
        @keyframes gift-amount-slide {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes gift-tipper-fade {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

/**
 * Global orchestrator: fetches pending gifts on mount, subscribes to WS
 * `tip_received` events, and plays takeovers in sequence.
 */
export default function GiftShowcase() {
  const { user } = useAuth();
  const { subscribe } = useWS();
  const [queue, setQueue] = useState([]);
  const [customAnimations, setCustomAnimations] = useState([]);
  // Ref mirror so the fetcher can read the current animations without
  // reacting to state changes (used by the tip event handlers).
  const customRef = useRef([]);
  useEffect(() => { customRef.current = customAnimations; }, [customAnimations]);

  // Fetch the admin-uploaded boost MP4 catalog. Runs on mount, on window
  // focus (so uploading in a second tab then returning here refreshes),
  // and right before each incoming boost is enqueued — otherwise a fresh
  // upload never plays until the user hard-reloads.
  const refreshCustom = useCallback(async () => {
    try {
      const r = await api.get("/gifts/tiers");
      const list = r.data?.custom_animations || [];
      setCustomAnimations(list);
      customRef.current = list;
      return list;
    } catch {
      return customRef.current;
    }
  }, []);

  useEffect(() => {
    refreshCustom();
    const onFocus = () => { refreshCustom(); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshCustom]);

  // Fetch backlog on login/mount
  useEffect(() => {
    if (!user) {
      setQueue([]);
      return;
    }
    api
      .get("/me/pending-gifts")
      .then((r) => {
        const gifts = r.data.gifts || [];
        if (gifts.length) {
          // Any pending boosts may match a newly-uploaded MP4 — refetch
          // catalog before flushing them into the queue.
          refreshCustom();
          setQueue((q) => [...q, ...gifts]);
        }
      })
      .catch(() => {});
  }, [user, refreshCustom]);

  // Live enqueue on WS tip_received (only for the current viewer)
  useEffect(() => {
    if (!user) return undefined;
    return subscribe((evt) => {
      if (evt.type !== "tip_received") return;
      if ((evt.amount_cents || 0) < 100) return;
      const gift = {
        id: evt.tip_id,
        amount_cents: evt.amount_cents,
        tipper_name: evt.tipper_name,
        tipper_picture: evt.tipper_picture,
        message: evt.message,
      };
      // Refresh the MP4 catalog before enqueueing so a boost immediately
      // after an admin upload picks up the new animation. Fire-and-forget
      // — the takeover reads whichever list has settled by the time it
      // computes `customVideoUrl` via useMemo.
      refreshCustom();
      setQueue((q) => [...q, gift]);
    });
  }, [subscribe, user, refreshCustom]);

  // Manual enqueue via a global CustomEvent so any page can trigger it
  // (e.g. VideoView after a Stripe checkout returns and the tip is credited).
  useEffect(() => {
    const handler = (e) => {
      const g = e.detail;
      if (!g || !g.amount_cents || g.amount_cents < 100) return;
      refreshCustom();
      setQueue((q) => [...q, g]);
    };
    window.addEventListener("neosync:gift-preview", handler);
    return () => window.removeEventListener("neosync:gift-preview", handler);
  }, [refreshCustom]);

  if (queue.length === 0) return null;
  const current = queue[0];

  const advance = () => {
    // Ack only "received" gifts (creators own them) — guest tippers or
    // sent-mode previews have no server-side ownership to update.
    if (current.id && current.mode !== "sent" && user) {
      api.post(`/me/pending-gifts/${current.id}/ack`).catch(() => {});
    }
    setQueue((q) => q.slice(1));
  };

  return <Takeover key={current.id || `${current.amount_cents}-${queue.length}`} gift={current} onDismiss={advance} customAnimations={customAnimations} />;
}
